import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { CircularProgress, FormControl, Input, InputAdornment } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";

/**
 * Input component stylized for search
 */

const useStyles = makeStyles((theme) => ({
  marginLeft: {
    marginLeft: theme.spacing(1),
  },
  fullWidth: {
    display: "flex",
    width: "auto",
  },
  textField: {
    background: theme.palette.background.overlay,
    padding: "4px",
  },
}));

const Search = ({
  debounce,
  onChange,
  placeholder,
  loading,
  autoFocus,
  onFocus,
  onBlur,
  fullWidth,
  disabled,
  inputProps,
}) => {
  const [text, setText] = useState("");
  const theme = useTheme();
  const classes = useStyles(theme);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return () => {};
    }
    if (debounce) {
      const timeout = setTimeout(() => {
        onChange(text);
      }, debounce);
      return () => clearTimeout(timeout);
    }
    return onChange(text);
  }, [text]);

  return (
    <FormControl className={clsx(fullWidth && classes.fullWidth)}>
      <Input
        id="search-input"
        className={clsx(classes.textField)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        fullWidth
        autoComplete="off"
        inputProps={{ ...inputProps, "aria-label": placeholder, onFocus, onBlur }}
        value={text}
        disabled={disabled}
        onChange={(e) => setText(e.target.value)}
        startAdornment={
          <InputAdornment position="start" className={classes.marginLeft}>
            {loading ? (
              <CircularProgress data-testid="spinner" color="inherit" size={20} />
            ) : (
              <SearchIcon data-testid="search-icon" />
            )}
          </InputAdornment>
        }
      />
    </FormControl>
  );
};

Search.propTypes = {
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  debounce: PropTypes.number,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  autoFocus: PropTypes.bool,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  inputProps: PropTypes.object,
};

Search.defaultProps = {
  onChange: () => {},
  onFocus: () => {},
  onBlur: () => {},
  debounce: null,
  placeholder: "Search...",
  loading: false,
  autoFocus: true,
  fullWidth: true,
  disabled: false,
  inputProps: {},
};

export default Search;
